import { drawText as canvasDrawText, measureTextDimensions } from '@/services/canvas/text/CanvasTextService';
import type { FontConfig } from '@/services/canvas/text/CanvasTextService';
import useColorUtils from '@/utils/colorUtils';
import { BORDER_STYLES } from '@/models/BORDER_STYLES';
import { TEXT_POSITION } from '@/models/TEXT_POSITION';
import {
  EDGE_LINES,
  calculateBoundsForEdge,
  getEdgeType,
} from '@/modules/Edge';
import type {
  EdgeLineBounds,
  EdgeArrowsBounds,
  EdgeShadowBounds,
  EdgeTitleBounds,
  EdgeTypeValue,
  TextAnchor,
  EdgeRoutingContext,
  WorldPathResult,
} from '@/modules/Edge';

const { hexToRGB, RGB_to_hex } = useColorUtils();

interface EdgeContent {
  start: string | null;
  middle: string | null;
  end: string | null;
}

interface ParsedContent {
  content: EdgeContent;
  amount: number;
}

interface DrawCircleSettings {
  ctx: CanvasRenderingContext2D;
  x: number;
  y: number;
  radius: number;
  startAngle: number;
  endAngle: number;
  couterclockwise: boolean;
  fillStyle: string;
  strokeStyle: string;
  lineWidth: number;
}

interface TextMeasurement {
  ctx: CanvasRenderingContext2D;
  title: string;
  x: number;
  y: number;
  width: number;
  height: number | null;
  vAlign: string | null;
}

export interface EdgeTemplateConfig {
  id: number;
  type: string;
  name: string;
  routingType?: EdgeTypeValue;
  lineStart: string;
  lineStartSize: string | number;
  lineEnd: string;
  lineEndSize: string | number;
  border: boolean;
  borderColor: string;
  borderStyle: string;
  borderWidth: number;
  fontColor: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  textPosition: string;
  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;
  contentMiddle: string | null;
}

export default class EdgeTemplate {
  id: number;
  type: string;
  name: string;
  routingType: EdgeTypeValue;

  lineStart: string;
  lineStartSize: string | number;
  lineEnd: string;
  lineEndSize: string | number;

  startPosX: number;
  endPosX: number;
  startPosY: number;
  endPosY: number;

  xCache: number | null;
  yCache: number | null;

  shadow: boolean;
  shadowColor: string;
  shadowX: number;
  shadowY: number;
  shadowBlur: number;

  border: boolean;
  borderColor: string;
  borderStyle: string;
  borderWidth: number;
  fontColor: string;
  fontSize: number;
  fontFamily: string;
  fontWeight: string;
  fontStyle: string;
  textPosition: string;

  contentStart: string;
  contentMiddle: string | null;
  contentEnd: string;
  content?: EdgeContent | null;

  _HOVER_AREA: number;
  hover: boolean;
  selected: boolean;
  locked: boolean;

  path: Path2D | null;
  pathInteraction: Path2D | null;

  _ARROW_HEIGHT: number;
  _ARROW_DOT_RADIUS: number;

  _currentColor: string;

  ctx: CanvasRenderingContext2D | null;
  pixelRatio: number | null;
  cacheInstance: HTMLCanvasElement | null;
  snapshot: string | null;

  edgeBounds: EdgeLineBounds | null;
  arrowsBounds: EdgeArrowsBounds | null;
  shadowBounds: EdgeShadowBounds | null;
  titleBounds: EdgeTitleBounds | null;

  currentLayerId: number;
  visibilityMin: number;
  visibilityMax: number;

  ZOOM_MAX: number;
  _SHADOW_BLUR_MULTIPLIER: number;
  _LABEL_PADDING: number;
  _BASE_CANVAS_SIZE: number;

  hidden: boolean;
  editing: boolean;

  constructor({
    id, type, name, routingType,
    lineStart, lineStartSize, lineEnd, lineEndSize,
    border, borderColor, borderStyle, borderWidth,
    fontColor, fontSize, fontFamily, fontWeight, fontStyle, textPosition,
    shadow, shadowColor, shadowX, shadowY, shadowBlur,
    contentMiddle
  }: EdgeTemplateConfig) {
    this.id = id;
    this.type = type;
    this.name = name;
    this.routingType = routingType ?? 'straight';

    this.lineStart = lineStart;
    this.lineStartSize = lineStartSize;
    this.lineEnd = lineEnd;
    this.lineEndSize = lineEndSize;

    this.startPosX = 0;
    this.endPosX = 112;
    this.startPosY = 56;
    this.endPosY = 56;

    this.xCache = null;
    this.yCache = null;

    this.shadow = shadow;
    this.shadowColor = shadowColor;
    this.shadowX = shadowX;
    this.shadowY = shadowY;
    this.shadowBlur = shadowBlur;

    this.border = border;
    this.borderColor = borderColor;
    this.borderStyle = borderStyle;
    this.borderWidth = borderWidth;
    this.fontColor = fontColor || '#000000';
    this.fontSize = fontSize;
    this.fontFamily = fontFamily;
    this.fontWeight = fontWeight;
    this.fontStyle = fontStyle;
    this.textPosition = textPosition;

    this.contentStart = "";
    this.contentMiddle = contentMiddle;
    this.contentEnd = "";

    this._HOVER_AREA = 20;
    this.hover = false;
    this.selected = false;
    this.locked = false;

    this.path = null;
    this.pathInteraction = null;

    this._ARROW_HEIGHT = 12;
    this._ARROW_DOT_RADIUS = 6;

    this._currentColor = this.borderColor;

    this.ctx = null;
    this.pixelRatio = null;
    this.cacheInstance = null;
    this.snapshot = null;

    this.edgeBounds = null;
    this.arrowsBounds = null;
    this.shadowBounds = null;
    this.titleBounds = null;

    this.currentLayerId = 1;
    this.visibilityMin = 1;
    this.visibilityMax = 1;

    this.ZOOM_MAX = 1;
    this._SHADOW_BLUR_MULTIPLIER = 2;
    this._LABEL_PADDING = 10;
    this._BASE_CANVAS_SIZE = 112;

    this.hidden = false;
    this.editing = false;
  }

  cacheInstanceSetDimensions(): void {
    // Ensure canvas and ctx exist before bounds calculation (text measurement needs ctx)
    if (!this.cacheInstance) {
      this.cacheInstance = document.createElement('canvas');
      this.ctx = this.cacheInstance.getContext('2d');
    }

    // Compute bounds (also sets fromPoint/toPoint as a side effect)
    const { width, height } = calculateBoundsForEdge(this);

    // World-space edges don't use a cache canvas — bounds are computed for connection points only
    if (this._isWorldSpace() && !this.name) return;

    this.getPixelRatio();

    this.cacheInstance.width = Math.floor(width! * this.ZOOM_MAX * this.pixelRatio!);
    this.cacheInstance.height = Math.floor(height! * this.ZOOM_MAX * this.pixelRatio!);

    this.cacheInstance.style.width = `${width! * this.ZOOM_MAX}px`;
    this.cacheInstance.style.height = `${height! * this.ZOOM_MAX}px`;

    this.ctx!.scale(this.pixelRatio! * this.ZOOM_MAX, this.pixelRatio! * this.ZOOM_MAX);
  }

  cache(_layerID?: number, _ctxPath?: CanvasRenderingContext2D | null, _selectedID?: number | null): void {
    this.getPixelRatio();
    ({ x: this.xCache, y: this.yCache } = this._getEdgeCoordinates());

    this._drawEdge();
    this._drawArrows();
    this._drawText();
  }

  cacheClear(): void {
    if (!this.ctx || !this.cacheInstance) return;
    this.ctx.clearRect(0, 0, this.cacheInstance.width, this.cacheInstance.height);
  }

  getPixelRatio(): number {
    return this.pixelRatio = window.devicePixelRatio;
  }

  makeSnapshot(canvas?: HTMLCanvasElement): void {
    if (!canvas) canvas = this.cacheInstance!;
    this.snapshot = this.toDataURL(canvas);
  }

  toDataURL(canvas?: HTMLCanvasElement): string {
    if (!canvas) canvas = this.cacheInstance!;
    return canvas.toDataURL();
  }

  // DRAWING
  _drawEdge(): void {
    if (this.hidden) return;
    this.ctx!.beginPath();
    this.ctx!.save();

    this._createLine();

    this._drawShadow();

    this.ctx!.stroke();
    this.ctx!.restore();

    if (this.editing) {
      this._drawEditing();
    }
    this.ctx!.closePath();
  }

  _drawEditing(): void {
    const { width: edgeWidth } = this.edgeBounds!;
    const { startArrowWidth, endArrowWidth } = this.arrowsBounds!;

    this.ctx!.save();

    const STATUS_LAYER_COLOR = 'rgba(60, 60, 60, .54)';
    this.ctx!.lineWidth = this.borderWidth + this._HOVER_AREA;
    this.ctx!.strokeStyle = STATUS_LAYER_COLOR;
    this.ctx!.globalAlpha = 1;

    this.ctx!.moveTo(this.xCache! + startArrowWidth, this.yCache!);
    this.ctx!.lineTo(this.xCache! + edgeWidth - endArrowWidth, this.yCache!);

    this.ctx!.stroke();
    this.ctx!.restore();
  }

  _createLine(): void {
    const { width: edgeWidth } = this.edgeBounds!;
    const { startArrowWidth, endArrowWidth } = this.arrowsBounds!;

    if (this.borderStyle === BORDER_STYLES.DOTTED) {
      this.ctx!.lineCap = 'round';
      const multiplier = this.borderWidth === 1 ? 2 : 1;
      this.ctx!.lineWidth = this.borderWidth + 1;
      this.ctx!.setLineDash([this.borderWidth / 100, this.borderWidth * multiplier * 2.4]);
    }

    if (this.borderStyle === BORDER_STYLES.DASHED) {
      this.ctx!.setLineDash([this.borderWidth * 4, this.borderWidth * 2]);
    }

    if (this.name) {
      this.ctx!.lineWidth = this.borderWidth > 4 ? 4 : this.borderWidth;
    } else this.ctx!.lineWidth = this.borderWidth;

    this.ctx!.strokeStyle = this._currentColor;
    this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);

    const edgeType = getEdgeType(this.routingType);
    edgeType.createPath(this.ctx!, this.xCache! + startArrowWidth, this.yCache!, this.xCache! + edgeWidth - endArrowWidth, this.yCache!);
  }

  _ctxRotate(angle: number, x: number, y: number, ctx?: CanvasRenderingContext2D): void {
    const target = ctx ? ctx : this.ctx!;
    target.translate(x, y);
    target.rotate(angle);
    target.translate(-x, -y);
  }

  _drawShadow(): void {
    const reversedMultiplier = this.endPosX - this.startPosX < 0 ? -1 : 1;
    if (this.shadow) {
      this.ctx!.shadowColor = this.shadowColor;
      this.ctx!.shadowOffsetX = this.shadowX;
      this.ctx!.shadowOffsetY = this.shadowY * reversedMultiplier;
      this.ctx!.shadowBlur = this.shadowBlur * this._SHADOW_BLUR_MULTIPLIER;
    }
  }

  _drawCircle(settings: DrawCircleSettings): void {
    const { ctx, x, y, radius, startAngle, endAngle, couterclockwise, fillStyle, strokeStyle, lineWidth } = settings;

    ctx.fillStyle = fillStyle;
    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;

    ctx.beginPath();
    ctx.arc(
      x,
      y,
      radius,
      startAngle,
      endAngle,
      couterclockwise
    );
    ctx.closePath();

    ctx.fill();
    ctx.stroke();
  }

  _drawArrow(arrow: string, x: number, y: number, r: number): void {
    switch (arrow) {
      case EDGE_LINES.SHARP.VALUE: {
        this.ctx!.save();
        this.ctx!.beginPath();
        this.ctx!.moveTo(x - r / 3, y - r / 1.5);
        this.ctx!.lineTo(x + r, y);
        this.ctx!.lineTo(x - r / 3, y + r / 1.5);
        this.ctx!.lineTo(x, y);
        this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);
        this.ctx!.fillStyle = this._currentColor;
        this._drawShadow();
        this.ctx!.fill();
        this.ctx!.closePath();
        this.ctx!.moveTo(x, y);
        this.ctx!.restore();
        break;
      }
      case EDGE_LINES.STRAIGHT.VALUE: {
        this.ctx!.save();
        this.ctx!.beginPath();
        this.ctx!.moveTo(x - r / 2, y - r / 1.5);
        this.ctx!.lineTo(x + r, y);
        this.ctx!.lineTo(x - r / 2, y + r / 1.5);
        this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);
        this.ctx!.fillStyle = this._currentColor;
        this._drawShadow();
        this.ctx!.fill();
        this.ctx!.closePath();
        this.ctx!.restore();
        break;
      }
      case EDGE_LINES.WIDE.VALUE: {
        this.ctx!.save();
        this.ctx!.beginPath();
        this.ctx!.lineWidth = (1 + this.borderWidth);
        this.ctx!.moveTo(x + r / 3 - this.ctx!.lineWidth / 2, y - r / 1.7);
        this.ctx!.lineTo(x + r - this.ctx!.lineWidth / 2, y);
        this.ctx!.lineTo(x + r / 3 - this.ctx!.lineWidth / 2, y + r / 1.7);

        this.ctx!.lineCap = 'square';
        this.ctx!.lineJoin = 'miter';

        this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);
        this.ctx!.strokeStyle = this._currentColor;
        this._drawShadow();

        this.ctx!.stroke();
        this.ctx!.closePath();
        this.ctx!.restore();
        break;
      }
    }
  }

  _drawDot(x: number, y: number, radius: number): void {
    this.ctx!.beginPath();
    this.ctx!.arc(
      x, y,
      radius,
      0,
      Math.PI * 2,
      false
    );
    this.ctx!.fillStyle = this._currentColor;
    this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);
    this._drawShadow();
    this.ctx!.fill();
    this.ctx!.closePath();
  }

  _drawArrows(): void {
    let borderWidth = this.borderWidth;
    if (this.name) borderWidth = this.borderWidth > 4 ? 4 : this.borderWidth;
    const _arrowHeight = this._ARROW_HEIGHT * borderWidth;
    const dotRadius = this._ARROW_DOT_RADIUS * borderWidth;
    const fromSizeMultiplier = parseInt(String(this.lineStartSize)) ? Number(this.lineStartSize) / 100 : 1;
    const toSizeMultiplier = parseInt(String(this.lineEndSize)) ? Number(this.lineEndSize) / 100 : 1;

    const { width: edgeWidth } = this.edgeBounds!;

    this.ctx!.save();
    this.ctx!.imageSmoothingEnabled = true;

    if (this.lineStart !== EDGE_LINES.NONE.VALUE) {
      if (this.lineStart === EDGE_LINES.DOT.VALUE) {
        this._drawDot(
          this.xCache! + dotRadius * fromSizeMultiplier / 2,
          this.yCache!,
          dotRadius * fromSizeMultiplier / 2,
        );
      } else {
        this.ctx!.save();
        this._ctxRotate(
          Math.PI,
          this.xCache! + _arrowHeight * fromSizeMultiplier / 2,
          this.yCache!
        );
        this._drawArrow(
          this.lineStart,
          this.xCache! + _arrowHeight * fromSizeMultiplier / 2,
          this.yCache!,
          _arrowHeight * fromSizeMultiplier / 2,
        );
        this.ctx!.restore();
      }
    }

    if (this.lineEnd !== EDGE_LINES.NONE.VALUE) {
      if (this.lineEnd === EDGE_LINES.DOT.VALUE) {
        this._drawDot(
          this.xCache! + edgeWidth - dotRadius * toSizeMultiplier / 2,
          this.yCache!,
          dotRadius * toSizeMultiplier / 2,
        );
      } else {
        this.ctx!.save();
        this._drawArrow(
          this.lineEnd,
          this.xCache! + edgeWidth - _arrowHeight * toSizeMultiplier / 2,
          this.yCache!,
          _arrowHeight * toSizeMultiplier / 2,
        );
        this.ctx!.restore();
      }
    }

    this.ctx!.restore();
  }

  getVisibility(layerID: number): void {
    this._getVisibility(layerID);
  }

  _getVisibility(layerID?: number): number {
    layerID = layerID || this.currentLayerId;
    return layerID < this.visibilityMin || layerID > this.visibilityMax ? 0.5 : 1;
  }

  _drawText(): void {
    let contentParsed: ParsedContent =
      this.content ?
        this._contentParsed(this.content) :
        {
          amount: 1,
          content: { start: null, middle: this.contentMiddle, end: null }
        };

    const labelsTotal = contentParsed.amount;
    let content: Record<string, string | null> = contentParsed.content;
    const labelMinWidth = 30;
    const { startLabelPadding, endLabelPadding } = this.arrowsBounds!;

    let fontSize = this.fontSize;
    if (!this.name) fontSize = this.fontSize > 20 ? 20 : this.fontSize;

    if (!this.hidden && labelsTotal) {
      const textLineHeight = fontSize;

      const edgeFontConfig: FontConfig = {
        fontSize,
        fontFamily: this.fontFamily,
        fontWeight: this.fontWeight,
        fontStyle: this.fontStyle,
        lineHeight: textLineHeight,
      };

      const { x: edgeX, y: edgeY, width: edgeWidth } = this.edgeBounds!;
      const { y: titleY, height: titleHeight, vAlign: titleVAlign } = this.titleBounds!;

      if (this.fontColor.includes('rgba')) {
        this.ctx!.fillStyle = this.fontColor;
      } else {
        const fontInRGB = this.fontColor.includes('rgb') ? hexToRGB(RGB_to_hex(this.fontColor)) : hexToRGB(this.fontColor);
        this.ctx!.fillStyle = `rgba(${fontInRGB.r}, ${fontInRGB.g}, ${fontInRGB.b}, 1)`;
      }

      this.ctx!.globalAlpha = this._getVisibility(this.currentLayerId);

      const LABEL_ALIGN: Record<string, string> = {
        START: 'left',
        MIDDLE: 'center',
        END: 'right'
      };

      let step = 0;

      if (this.endPosX - this.startPosX < 0) {
        const properties: Record<string, string | null> = {};
        Object.entries(content).reverse().forEach(([key, value]) => Object.assign(properties, { [key]: value }));
        content = properties;
        LABEL_ALIGN.START = 'right';
        LABEL_ALIGN.END = 'left';
      }

      this.ctx!.save();

      if (this.endPosX - this.startPosX < 0) {
        this._ctxRotate(
          180 * Math.PI / 180,
          edgeWidth / 2,
          edgeY
        );
      }

      const labelWidth = (edgeWidth - startLabelPadding - endLabelPadding) / contentParsed.amount;
      const computedArrowWidth = this.endPosX - this.startPosX < 0 ? endLabelPadding : startLabelPadding;
      const computedEdgeX = this.endPosX - this.startPosX < 0 ? -this.xCache! : this.xCache!;
      const computedTitleY = this.endPosX - this.startPosX < 0 ? titleY! - this.yCache! : titleY! + this.yCache!;

      for (const [label, labelText] of Object.entries(content)) {
        if (labelText) {
          const textMeasurement: TextMeasurement = {
            ctx: this.ctx!,
            title: labelText,
            x: computedEdgeX + computedArrowWidth + labelWidth * step,
            y: computedTitleY,
            width: labelWidth,
            height: titleHeight,
            vAlign: titleVAlign ?? null
          };
          step++;

          if (labelWidth >= labelMinWidth) {
            this._drawContentEdge(edgeFontConfig, textMeasurement, LABEL_ALIGN[label.toUpperCase()]);
          }
        }
      }

      this.ctx!.restore();
    }
  }

  _contentParsed(content: EdgeContent): ParsedContent {
    const parsedContent: EdgeContent = {
      start: null,
      middle: null,
      end: null
    };
    let amount = 0;
    for (const [type, value] of Object.entries(content)) {
      if (value?.trim()) {
        (parsedContent as Record<string, string | null>)[type] = value;
        amount++;
      }
    }
    return { content: parsedContent, amount };
  }

  _drawContentEdge(fontConfig: FontConfig, labelData: TextMeasurement, align: string): void {
    const targetCtx = labelData.ctx;
    targetCtx.save();
    canvasDrawText(
      targetCtx,
      labelData.title,
      { x: labelData.x, y: labelData.y, width: labelData.width, height: labelData.height },
      fontConfig,
      { align, vAlign: labelData.vAlign ?? 'center' },
    );
    targetCtx.restore();
  }

  _radiansToDegrees(rad: number): string {
    if (rad < 0) {
      return (360.0 + (rad * (180 / Math.PI))).toFixed(2);
    } else {
      return (rad * (180 / Math.PI)).toFixed(2);
    }
  }

  _degreesToRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  // BOUNDS

  _getEdgeCoordinates(): { x: number; y: number } {
    let x = this.edgeBounds!.x;
    let y = this.edgeBounds!.y;
    const shift = { x: 0, y: 0 };
    const isReversed = this.endPosX - this.startPosX < 0;

    const { shadowShiftX, shadowShiftY, blurWidth: shadowBlurWidth } = this.shadowBounds!;
    const { startArrowHeight, endArrowHeight } = this.arrowsBounds!;
    const { y: titleY, shiftReversed: titleShiftReversed } = this.titleBounds!;
    const editingHeight = this.editing ? this.borderWidth + this._HOVER_AREA : this.borderWidth;

    const shadowShiftXComputed = shadowShiftX! - shadowBlurWidth!;
    const shadowShiftYComputed = isReversed ? -(shadowShiftY! + shadowBlurWidth! + Math.max(startArrowHeight, endArrowHeight) / 2) : shadowShiftY! - shadowBlurWidth!;

    const titleComputed = isReversed ? titleShiftReversed : titleY!;

    shift.x = Math.abs(Math.min(0, shadowShiftXComputed));
    shift.y = Math.abs(Math.min(0, -editingHeight / 2, shadowShiftYComputed, titleComputed, -(startArrowHeight + this.borderWidth) / 2, -(endArrowHeight + this.borderWidth) / 2));

    x += shift.x;
    y += shift.y;

    return { x, y };
  }

  // WORLD-SPACE RENDERING (for non-straight edge types)

  _drawWorldEdge(
    ctx: CanvasRenderingContext2D,
    fromX: number, fromY: number,
    toX: number, toY: number,
    routingCtx?: EdgeRoutingContext
  ): WorldPathResult | null {
    if (this.hidden) return null;

    const edgeType = getEdgeType(this.routingType);
    if (!edgeType.createWorldPath && !edgeType.createWorldPathWithContext) return null;

    // Get tangent directions for path offset (arrow gap)
    const directAngle = Math.atan2(toY - fromY, toX - fromX);
    let startAngle = directAngle;
    let endAngle = directAngle;

    if (edgeType.createWorldPathWithContext && routingCtx) {
      // Use node CENTERS (not connection points) to determine exit/entry sides.
      // Connection point angles shift as nodes move, but center-to-center angle
      // is stable within the same quadrant — matching the actual routing direction.
      const centerAngle = Math.atan2(
        routingCtx.toCenterY - routingCtx.fromCenterY,
        routingCtx.toCenterX - routingCtx.fromCenterX
      );
      startAngle = this._snapToAxis(centerAngle);
      endAngle = this._snapToAxis(centerAngle + Math.PI) + Math.PI;
    }

    // Compute arrow offsets to shorten the path so it connects to arrow bases
    const { startOffset, endOffset } = this._getArrowOffsets();

    // Adjust path endpoints to account for arrows
    const pathFromX = fromX + Math.cos(startAngle) * startOffset;
    const pathFromY = fromY + Math.sin(startAngle) * startOffset;
    const pathToX = toX - Math.cos(endAngle) * endOffset;
    const pathToY = toY - Math.sin(endAngle) * endOffset;

    ctx.save();
    this._applyLineStyle(ctx);

    if (this.shadow) {
      ctx.shadowColor = this.shadowColor;
      ctx.shadowOffsetX = this.shadowX;
      ctx.shadowOffsetY = this.shadowY;
      ctx.shadowBlur = this.shadowBlur * this._SHADOW_BLUR_MULTIPLIER;
    }

    let path: Path2D;
    let startTangent: number;
    let endTangent: number;

    if (edgeType.createWorldPathWithContext && routingCtx) {
      const result = edgeType.createWorldPathWithContext(ctx, pathFromX, pathFromY, pathToX, pathToY, routingCtx);
      path = result.path;
      startTangent = result.startTangent;
      endTangent = result.endTangent;
    } else {
      path = edgeType.createWorldPath!(ctx, pathFromX, pathFromY, pathToX, pathToY);
      startTangent = edgeType.getTangentAtStart?.(fromX, fromY, toX, toY) ?? Math.atan2(toY - fromY, toX - fromX);
      endTangent = edgeType.getTangentAtEnd?.(fromX, fromY, toX, toY) ?? Math.atan2(toY - fromY, toX - fromX);
    }

    ctx.stroke(path);
    ctx.restore();

    if (this.editing) {
      this._drawWorldEditing(ctx, path);
    }

    return { path, startTangent, endTangent };
  }

  _getArrowOffsets(): { startOffset: number; endOffset: number } {
    let borderWidth = this.borderWidth;
    if (this.name) borderWidth = this.borderWidth > 4 ? 4 : this.borderWidth;
    const _arrowHeight = this._ARROW_HEIGHT * borderWidth;
    const dotRadius = this._ARROW_DOT_RADIUS * borderWidth;
    const fromSizeMultiplier = parseInt(String(this.lineStartSize)) ? Number(this.lineStartSize) / 100 : 1;
    const toSizeMultiplier = parseInt(String(this.lineEndSize)) ? Number(this.lineEndSize) / 100 : 1;

    let startOffset = 0;
    if (this.lineStart !== EDGE_LINES.NONE.VALUE) {
      startOffset = this.lineStart === EDGE_LINES.DOT.VALUE
        ? dotRadius * fromSizeMultiplier / 2
        : _arrowHeight * fromSizeMultiplier / 2;
    }

    let endOffset = 0;
    if (this.lineEnd !== EDGE_LINES.NONE.VALUE) {
      endOffset = this.lineEnd === EDGE_LINES.DOT.VALUE
        ? dotRadius * toSizeMultiplier / 2
        : _arrowHeight * toSizeMultiplier / 2;
    }

    return { startOffset, endOffset };
  }

  _drawWorldEditing(ctx: CanvasRenderingContext2D, path: Path2D): void {
    ctx.save();
    const STATUS_LAYER_COLOR = 'rgba(60, 60, 60, .54)';
    ctx.lineWidth = this.borderWidth + this._HOVER_AREA;
    ctx.strokeStyle = STATUS_LAYER_COLOR;
    ctx.globalAlpha = 1;
    ctx.stroke(path);
    ctx.restore();
  }

  _drawWorldArrows(
    ctx: CanvasRenderingContext2D,
    fromX: number, fromY: number,
    toX: number, toY: number,
    startTangent?: number, endTangent?: number
  ): void {
    // Use tangent overrides when provided (e.g., stairs exit direction),
    // otherwise fall back to direct angle between endpoints.
    const directAngle = Math.atan2(toY - fromY, toX - fromX);
    const sAngle = startTangent ?? directAngle;
    const eAngle = endTangent ?? directAngle;

    let borderWidth = this.borderWidth;
    if (this.name) borderWidth = this.borderWidth > 4 ? 4 : this.borderWidth;
    const _arrowHeight = this._ARROW_HEIGHT * borderWidth;
    const dotRadius = this._ARROW_DOT_RADIUS * borderWidth;
    const fromSizeMultiplier = parseInt(String(this.lineStartSize)) ? Number(this.lineStartSize) / 100 : 1;
    const toSizeMultiplier = parseInt(String(this.lineEndSize)) ? Number(this.lineEndSize) / 100 : 1;

    ctx.save();
    ctx.imageSmoothingEnabled = true;

    if (this.lineStart !== EDGE_LINES.NONE.VALUE) {
      if (this.lineStart === EDGE_LINES.DOT.VALUE) {
        this._drawWorldDot(ctx, fromX, fromY, dotRadius * fromSizeMultiplier / 2);
      } else {
        const r = _arrowHeight * fromSizeMultiplier / 2;
        const cosS = Math.cos(sAngle);
        const sinS = Math.sin(sAngle);
        // Offset center outward by r so arrow tip lands exactly at fromPoint
        const cx = fromX + r * cosS;
        const cy = fromY + r * sinS;
        ctx.save();
        this._ctxRotate(sAngle + Math.PI, cx, cy, ctx);
        this._drawWorldArrow(ctx, this.lineStart, cx, cy, r);
        ctx.restore();
      }
    }

    if (this.lineEnd !== EDGE_LINES.NONE.VALUE) {
      if (this.lineEnd === EDGE_LINES.DOT.VALUE) {
        this._drawWorldDot(ctx, toX, toY, dotRadius * toSizeMultiplier / 2);
      } else {
        const r = _arrowHeight * toSizeMultiplier / 2;
        const cosE = Math.cos(eAngle);
        const sinE = Math.sin(eAngle);
        // Offset center outward by r so arrow tip lands exactly at toPoint
        const cx = toX - r * cosE;
        const cy = toY - r * sinE;
        ctx.save();
        this._ctxRotate(eAngle, cx, cy, ctx);
        this._drawWorldArrow(ctx, this.lineEnd, cx, cy, r);
        ctx.restore();
      }
    }

    ctx.restore();
  }

  _drawWorldArrow(ctx: CanvasRenderingContext2D, arrow: string, x: number, y: number, r: number): void {
    const alpha = this._getVisibility(this.currentLayerId);
    switch (arrow) {
      case EDGE_LINES.SHARP.VALUE: {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x - r / 3, y - r / 1.5);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x - r / 3, y + r / 1.5);
        ctx.lineTo(x, y);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = this._currentColor;
        ctx.fill();
        ctx.closePath();
        ctx.restore();
        break;
      }
      case EDGE_LINES.STRAIGHT.VALUE: {
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x - r / 2, y - r / 1.5);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x - r / 2, y + r / 1.5);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = this._currentColor;
        ctx.fill();
        ctx.closePath();
        ctx.restore();
        break;
      }
      case EDGE_LINES.WIDE.VALUE: {
        ctx.save();
        ctx.beginPath();
        ctx.lineWidth = (1 + this.borderWidth);
        ctx.moveTo(x + r / 3 - ctx.lineWidth / 2, y - r / 1.7);
        ctx.lineTo(x + r - ctx.lineWidth / 2, y);
        ctx.lineTo(x + r / 3 - ctx.lineWidth / 2, y + r / 1.7);
        ctx.lineCap = 'square';
        ctx.lineJoin = 'miter';
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = this._currentColor;
        ctx.stroke();
        ctx.closePath();
        ctx.restore();
        break;
      }
    }
  }

  _drawWorldDot(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2, false);
    ctx.fillStyle = this._currentColor;
    ctx.globalAlpha = this._getVisibility(this.currentLayerId);
    ctx.fill();
    ctx.closePath();
  }

  _drawWorldText(ctx: CanvasRenderingContext2D, fromX: number, fromY: number, toX: number, toY: number): void {
    const edgeType = getEdgeType(this.routingType);
    if (!edgeType.getTextAnchor) return;

    const contentParsed: ParsedContent =
      this.content ?
        this._contentParsed(this.content) :
        { amount: 1, content: { start: null, middle: this.contentMiddle, end: null } };

    if (!contentParsed.amount || !this.fontSize) return;

    const anchor: TextAnchor = edgeType.getTextAnchor(fromX, fromY, toX, toY);
    const fontSize = this.fontSize > 20 ? 20 : this.fontSize;

    const curvedFontConfig: FontConfig = {
      fontSize,
      fontFamily: this.fontFamily,
      fontWeight: this.fontWeight,
      fontStyle: this.fontStyle,
      lineHeight: fontSize * 1.1,
    };

    // Compute total label width from distance between endpoints
    const dx = toX - fromX;
    const dy = toY - fromY;
    const totalLength = Math.sqrt(dx * dx + dy * dy);
    const labelWidth = totalLength / contentParsed.amount;
    if (labelWidth < 30) return;

    if (this.fontColor.includes('rgba')) {
      ctx.fillStyle = this.fontColor;
    } else {
      const fontInRGB = this.fontColor.includes('rgb') ? hexToRGB(RGB_to_hex(this.fontColor)) : hexToRGB(this.fontColor);
      ctx.fillStyle = `rgba(${fontInRGB.r}, ${fontInRGB.g}, ${fontInRGB.b}, 1)`;
    }
    ctx.globalAlpha = this._getVisibility(this.currentLayerId);

    // Measure text height
    const labelsHeight: number[] = [];
    for (const [, labelText] of Object.entries(contentParsed.content)) {
      if (labelText) {
        const { textHeight } = measureTextDimensions(ctx, labelText, curvedFontConfig, labelWidth);
        labelsHeight.push(textHeight);
      }
    }
    const titleHeight = Math.max(...labelsHeight);
    const textPaddingFromEdge = 5 + (this.borderWidth - 1) / 2;

    ctx.save();

    // Rotate text to follow path angle, keeping it readable
    let textAngle = anchor.angle;
    if (textAngle > Math.PI / 2) textAngle -= Math.PI;
    if (textAngle < -Math.PI / 2) textAngle += Math.PI;

    this._ctxRotate(textAngle, anchor.x, anchor.y, ctx);

    // Position text above the edge at the anchor point
    const textY = anchor.y - titleHeight - textPaddingFromEdge;
    const textX = anchor.x - labelWidth * contentParsed.amount / 2;

    let step = 0;
    for (const [, labelText] of Object.entries(contentParsed.content)) {
      if (labelText) {
        const textMeasurement: TextMeasurement = {
          ctx,
          title: labelText,
          x: textX + labelWidth * step,
          y: textY,
          width: labelWidth,
          height: titleHeight,
          vAlign: TEXT_POSITION.BOTTOM.VALUE,
        };
        step++;
        this._drawContentEdge(curvedFontConfig, textMeasurement, 'center');
      }
    }

    ctx.restore();
  }

  _applyLineStyle(ctx: CanvasRenderingContext2D): void {
    if (this.borderStyle === BORDER_STYLES.DOTTED) {
      ctx.lineCap = 'round';
      const multiplier = this.borderWidth === 1 ? 2 : 1;
      ctx.lineWidth = this.borderWidth + 1;
      ctx.setLineDash([this.borderWidth / 100, this.borderWidth * multiplier * 2.4]);
    } else if (this.borderStyle === BORDER_STYLES.DASHED) {
      ctx.setLineDash([this.borderWidth * 4, this.borderWidth * 2]);
    } else {
      ctx.lineWidth = this.borderWidth;
    }

    ctx.strokeStyle = this._currentColor;
    ctx.globalAlpha = this._getVisibility(this.currentLayerId);
  }

  _isWorldSpace(): boolean {
    return getEdgeType(this.routingType).worldSpace === true;
  }

  _snapToAxis(angle: number): number {
    const a = Math.atan2(Math.sin(angle), Math.cos(angle));
    const q = Math.PI / 4;
    if (a >= -q && a < q) return 0;
    if (a >= q && a < 3 * q) return Math.PI / 2;
    if (a >= -3 * q && a < -q) return -Math.PI / 2;
    return Math.PI;
  }

  // DEV

  _devDrawBorders(): void {
    const multiplier = this.ZOOM_MAX * this.pixelRatio!;
    this.ctx!.moveTo(0, 0);
    this.ctx!.lineTo(this.ctx!.canvas.width / multiplier, 0);
    this.ctx!.lineTo(this.ctx!.canvas.width / multiplier, this.ctx!.canvas.height / multiplier);
    this.ctx!.lineTo(0, this.ctx!.canvas.height / multiplier);
    this.ctx!.lineTo(0, 0);
    this.ctx!.lineWidth = 2;
    this.ctx!.strokeStyle = 'rgba(255,244,255,.6)';
    this.ctx!.stroke();
  }
}
